import { useState } from 'react'
import { Database, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useSampleValues } from '@/hooks/use-data-tables'

interface SampleValuesPopoverProps {
  columnId: string
  columnName: string
}

export function SampleValuesPopover({ columnId, columnName }: SampleValuesPopoverProps) {
  const [open, setOpen] = useState(false)
  const { data, isLoading, isError } = useSampleValues(open ? columnId : '')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
          <Database className="h-3 w-3 mr-1" />
          Samples
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Sample Values &mdash; {columnName}</h4>
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading...
            </div>
          )}
          {isError && (
            <p className="text-sm text-destructive">Failed to load samples</p>
          )}
          {data && (
            <>
              {data.masked && (
                <p className="text-xs text-muted-foreground">Values are masked for security</p>
              )}
              <div className="space-y-1">
                {data.sampleValues.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sample values available</p>
                ) : (
                  data.sampleValues.map((value, i) => (
                    <code
                      key={i}
                      className="block text-xs font-mono bg-muted px-2 py-1 rounded truncate"
                    >
                      {value}
                    </code>
                  ))
                )}
              </div>
              {data.totalDistinct > 0 && (
                <p className="text-xs text-muted-foreground">
                  {data.totalDistinct} distinct values total
                </p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
